// Shared mock data for challenges

// ============================================
// MOCK RUN CHALLENGES FOR DISCOVER SCREEN
// These are displayed on Discover to show activity
// Always in "already started" state (start date in past)
// ============================================

// Helper to get a date N days in the past (YYYY-MM-DD format)
function getPastDate(daysAgo: number): string {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toISOString().split("T")[0];
}

// Helper to get a date N days in the future (YYYY-MM-DD format)
function getFutureDate(daysAhead: number): string {
  const date = new Date();
  date.setDate(date.getDate() + daysAhead);
  return date.toISOString().split("T")[0];
}

export interface MockDiscoverChallenge {
  id: string;
  name: string;
  description: string;
  type: "run" | "marathon";
  dailyGoal: number;
  durationDays: number;
  requiredDays: number;
  stakeAmount: number;
  startDate: string;
  endDate: string;
  status: "active";
  participantCount: number;
  participants: never[];
  currentUserParticipant: null;
  creatorId: string;
  // Marathon-specific fields
  targetDurationSeconds?: number | null;
  isOffChain?: boolean;
}

/**
 * Mock running challenges for Discover screen
 * Start dates are set in the past so they show as "Already Started"
 */
export const MOCK_RUN_CHALLENGES: MockDiscoverChallenge[] = [
  {
    id: "mock-run-1",
    name: "Morning Run Club",
    description:
      "Rise and shine with the SF Morning Run Club! This challenge is designed for early birds who want to kickstart their day with a refreshing run. Whether you're a seasoned runner or just getting started, this challenge will help you build a consistent morning routine. Join fellow runners who believe that the best way to start the day is with fresh air and movement. Track your progress, stay motivated, and become part of a community that celebrates every sunrise mile.",
    type: "run",
    dailyGoal: 3,
    durationDays: 21,
    requiredDays: 21,
    stakeAmount: 15,
    startDate: getPastDate(5), // Started 5 days ago
    endDate: getFutureDate(16), // 16 days remaining
    status: "active",
    participantCount: 88,
    participants: [],
    currentUserParticipant: null,
    creatorId: "mock-creator",
  },
  {
    id: "mock-run-2",
    name: "5K Daily Grind",
    description:
      "Push your limits with the 5K Daily Grind challenge! Commit to running 5 kilometers every single day for two weeks. This isn't just about distance—it's about building mental toughness and proving to yourself that consistency beats intensity. Perfect for intermediate runners looking to level up their game. The stakes are real, the community is supportive, and the results speak for themselves. Are you ready to grind?",
    type: "run",
    dailyGoal: 5,
    durationDays: 14,
    requiredDays: 14,
    stakeAmount: 25,
    startDate: getPastDate(3), // Started 3 days ago
    endDate: getFutureDate(11), // 11 days remaining
    status: "active",
    participantCount: 156,
    participants: [],
    currentUserParticipant: null,
    creatorId: "mock-creator",
  },
];

/**
 * Check if a challenge ID is a mock discover challenge
 */
export function isMockDiscoverChallenge(id: string): boolean {
  return id.startsWith("mock-run-");
}

/**
 * Get mock discover challenge by ID
 */
export function getMockDiscoverChallengeById(
  id: string
): MockDiscoverChallenge | null {
  return MOCK_RUN_CHALLENGES.find((c) => c.id === id) || null;
}

// ============================================
// EXISTING MOCK DATA FOR ACTIVE CHALLENGE DETAILS
// ============================================

export const MOCK_CHALLENGES_DATA = {
  "1": {
    id: "1",
    title: "30-Day Running Streak",
    stakeAmount: "$25.00",
    progress: 50,
    timeLeft: "15 Days Left",
    statusText: "Current Streak: 10 Days",
    currentDay: 15,
    totalDays: 30,
    daysLeft: 15,
    currentPot: "$1,500",
    yourStake: "$25.00",
    totalParticipants: 30,
    leaderboard: [
      {
        rank: 1,
        name: "Sarah J.",
        avatar:
          "https://lh3.googleusercontent.com/aida-public/AB6AXuCHsuP6kk8q5dZzSc_8LB0NVXemmhQCAoJu4d93eNcEMUfIoFL9Rv5GHH_vMWNBRSlUnfHzuaGKMfFwqU_v7s-azypGPv-oJ0m8DmH7V_HEjkSWlbypCZJLqEc8PSihy8G7pO4JJvOoEPxfmD1uN4cxETZQKgdSBVGaT7MsgoMve_S7LKPdlm4_K-Rmr8BRycYzWzuCfAIo5pKK4S7LzVZBTKAK-zaMYvhELU8Re0jua6_9RMYhlKczGEWGaSDXn8Aab-5g634uKz8m",
        currentProgress: 18,
        totalProgress: 30,
        isWinner: true,
      },
      {
        rank: 2,
        name: "Maria K.",
        avatar:
          "https://lh3.googleusercontent.com/aida-public/AB6AXuAxroj-aUh6T9o8LWqhVwShgy1laTfyXB4wob-8yQRtMEXjAlfC-37F8SSPbW-y9SnCWVK_RRQrpEiweKaXrYCtIhTTRg6_3ERZhPn_X9bqaucDsH649kTqO5wPW6dZluwPxflZH4SMzF823ZaA2joJCn9U1poEcHvhVgxRojUKHXQDGeYlFBcJtTA40ysVvRvJ5Rkvo-RMc8vl0GkvnLmaVuPya8JtSlU842iYG2_U4xWb18xYQrztz2auC-oxViXLYP2enKPGISgV",
        currentProgress: 17,
        totalProgress: 30,
      },
      {
        rank: 3,
        name: "Ben L.",
        avatar:
          "https://lh3.googleusercontent.com/aida-public/AB6AXuB5FilzU4FxkwzPT72MtR9kAJeRSOqw6e5F9X06gZZ22bvBsJxb_bqH0V-N2m74kUYG71uluM847q1JJI2SEDjaA-dlJH6FukCTR1iAPjBkhqmZFCzw379xGYMg3QdpoC2Jbp7uLmDxcBjkwIXaGIQeI_HGuhZsKzxL0mmTELL4cxmQtAA8GiYjD06W5H_gOcgC_zZ5hp5kHJ170t6lQSodgylbml5-oqVn0a71G67usLU_TRTI_QL9uzh6jErAjocWVgfLMeFRk8wa",
        currentProgress: 16,
        totalProgress: 30,
      },
      {
        rank: 4,
        name: "You",
        avatar:
          "https://lh3.googleusercontent.com/aida-public/AB6AXuDymDmkA1gaSpxj7gJ5FCEFLlp31mNaOxXVvNNe_4O9Qs_10j6Ji35xsOKwLF3Pl5MDxl_d7L0A4m_AyhrEQmRhFP0trRPMa9mGV0tMVQcoPYLTdOXZ_huFHuNQ5hPMHCk4jBVzF6LCl0z-DKieVu_1KpZUS-pm9kOOmXWSwyvuq1OzzFwFQfIkTO5Ri4yUDsjoCSgh-SOQ5wDnIfW0yfoBqE6qDyfPHW4l5ruZLOAik1P98C4qwcpriSlB95QnHt9Qwe0htiIo-kdP",
        currentProgress: 15,
        totalProgress: 30,
        isCurrentUser: true,
      },
      {
        rank: 5,
        name: "Alex P.",
        avatar:
          "https://lh3.googleusercontent.com/aida-public/AB6AXuBilDLodeFOHJFvd6Eygls9NxC8gOF52ry-tuLd3vI5scVTYtxXItRaDNJd05ZyJlBMlPCzjTOObCHATRyZEhYko0GwvOgtZbYqHxTitqhDMSTRlpoFQ4ht_o2ylb1LKkFTGsgQoMcNE-YHk3iTq3T1n2cGe1wMSiBMLU2mvHZNpMlA0m2Y1XTOMxMwzVXJqoRfYR9o1lvJ9HUswXCNnMkTl0pD_L0Hkfr8nOmWL_B10NtAlocyd9WTg3ocLO6N83dsE1wOh7Jzgamw",
        currentProgress: 14,
        totalProgress: 30,
      },
    ],
    participantAvatars: [
      "https://lh3.googleusercontent.com/aida-public/AB6AXuBilDLodeFOHJFvd6Eygls9NxC8gOF52ry-tuLd3vI5scVTYtxXItRaDNJd05ZyJlBMlPCzjTOObCHATRyZEhYko0GwvOgtZbYqHxTitqhDMSTRlpoFQ4ht_o2ylb1LKkFTGsgQoMcNE-YHk3iTq3T1n2cGe1wMSiBMLU2mvHZNpMlA0m2Y1XTOMxMwzVXJqoRfYR9o1lvJ9HUswXCNnMkTl0pD_L0Hkfr8nOmWL_B10NtAlocyd9WTg3ocLO6N83dsE1wOh7Jzgamw",
      "https://lh3.googleusercontent.com/aida-public/AB6AXuBbHsu0Bd6Qje8FCZRSZoHRBtbY2QtGQG8sbmCs4UufQ3G9nBhFNex7UpyWWbxs-l1oAMSXh3_t_fZelwv4kgfo0wiZwGuKlj2rYpdSOvzlJvf6Mkz1Kb_7gKSHxl_466OsdqBF-79X9lXbQMtz505-WifwxmtmxPgt47SE9XVNFugHzFHfwMzdBWhkrcjAuAhyWd21yNa7euBxAseojC_q9wtVWSDS4BZptCi7HLgU_ek8wdmEfvE9nqFB3OyQUyMq3OHok3NvDLPw",
      "https://lh3.googleusercontent.com/aida-public/AB6AXuCqY1PnQ14AV25TbTNJaFTtjcBiRhhcI_x2tuJn7Qbvz14OYqjk5jQ9vCAtLj2bGlvqSXGWF5Jfe0xAaDN_0qcVlsVLIqDeuusJX3Nx_K0Jn_6S_uUMEFualWqSRurBna1DJzxyEWbGw_d6YQg2JRwQ5icseXN63BJARrDAMsqGmrNMD2n5UOcCBmRNdNd31rBmu1a74P1mYXEJJ2FBh_7KHtGen7PoFdYgn_O8uw_KzRZbYA5megtO7F-h019fFfF-bEantqeVK6E9",
    ],
    userStats: {
      userAvatar:
        "https://lh3.googleusercontent.com/aida-public/AB6AXuDymDmkA1gaSpxj7gJ5FCEFLlp31mNaOxXVvNNe_4O9Qs_10j6Ji35xsOKwLF3Pl5MDxl_d7L0A4m_AyhrEQmRhFP0trRPMa9mGV0tMVQcoPYLTdOXZ_huFHuNQ5hPMHCk4jBVzF6LCl0z-DKieVu_1KpZUS-pm9kOOmXWSwyvuq1OzzFwFQfIkTO5Ri4yUDsjoCSgh-SOQ5wDnIfW0yfoBqE6qDyfPHW4l5ruZLOAik1P98C4qwcpriSlB95QnHt9Qwe0htiIo-kdP",
      totalDistance: 86.2,
      distanceGoal: 100,
      averagePace: "5'45\"",
      longestRun: 10.5,
      activeDays: 15,
      totalActiveDays: 15,
      recentActivities: [
        { label: "Today", distance: 5.2, status: "completed" as const },
        { label: "Yesterday", distance: 4.8, status: "completed" as const },
      ],
    },
  },
  "2": {
    id: "2",
    title: "Weekly Push-up Goal",
    stakeAmount: "$10.00",
    progress: 57,
    timeLeft: "3 Days Left",
    statusText: "4/7 Days Logged",
    currentDay: 4,
    totalDays: 7,
    daysLeft: 3,
    currentPot: "$450",
    yourStake: "$10.00",
    totalParticipants: 15,
    leaderboard: [
      {
        rank: 1,
        name: "Mike T.",
        avatar:
          "https://lh3.googleusercontent.com/aida-public/AB6AXuB5FilzU4FxkwzPT72MtR9kAJeRSOqw6e5F9X06gZZ22bvBsJxb_bqH0V-N2m74kUYG71uluM847q1JJI2SEDjaA-dlJH6FukCTR1iAPjBkhqmZFCzw379xGYMg3QdpoC2Jbp7uLmDxcBjkwIXaGIQeI_HGuhZsKzxL0mmTELL4cxmQtAA8GiYjD06W5H_gOcgC_zZ5hp5kHJ170t6lQSodgylbml5-oqVn0a71G67usLU_TRTI_QL9uzh6jErAjocWVgfLMeFRk8wa",
        currentProgress: 5,
        totalProgress: 7,
        isWinner: true,
      },
      {
        rank: 2,
        name: "Lisa M.",
        avatar:
          "https://lh3.googleusercontent.com/aida-public/AB6AXuCHsuP6kk8q5dZzSc_8LB0NVXemmhQCAoJu4d93eNcEMUfIoFL9Rv5GHH_vMWNBRSlUnfHzuaGKMfFwqU_v7s-azypGPv-oJ0m8DmH7V_HEjkSWlbypCZJLqEc8PSihy8G7pO4JJvOoEPxfmD1uN4cxETZQKgdSBVGaT7MsgoMve_S7LKPdlm4_K-Rmr8BRycYzWzuCfAIo5pKK4S7LzVZBTKAK-zaMYvhELU8Re0jua6_9RMYhlKczGEWGaSDXn8Aab-5g634uKz8m",
        currentProgress: 4,
        totalProgress: 7,
      },
      {
        rank: 3,
        name: "David K.",
        avatar:
          "https://lh3.googleusercontent.com/aida-public/AB6AXuAxroj-aUh6T9o8LWqhVwShgy1laTfyXB4wob-8yQRtMEXjAlfC-37F8SSPbW-y9SnCWVK_RRQrpEiweKaXrYCtIhTTRg6_3ERZhPn_X9bqaucDsH649kTqO5wPW6dZluwPxflZH4SMzF823ZaA2joJCn9U1poEcHvhVgxRojUKHXQDGeYlFBcJtTA40ysVvRvJ5Rkvo-RMc8vl0GkvnLmaVuPya8JtSlU842iYG2_U4xWb18xYQrztz2auC-oxViXLYP2enKPGISgV",
        currentProgress: 4,
        totalProgress: 7,
      },
      {
        rank: 4,
        name: "You",
        avatar:
          "https://lh3.googleusercontent.com/aida-public/AB6AXuDymDmkA1gaSpxj7gJ5FCEFLlp31mNaOxXVvNNe_4O9Qs_10j6Ji35xsOKwLF3Pl5MDxl_d7L0A4m_AyhrEQmRhFP0trRPMa9mGV0tMVQcoPYLTdOXZ_huFHuNQ5hPMHCk4jBVzF6LCl0z-DKieVu_1KpZUS-pm9kOOmXWSwyvuq1OzzFwFQfIkTO5Ri4yUDsjoCSgh-SOQ5wDnIfW0yfoBqE6qDyfPHW4l5ruZLOAik1P98C4qwcpriSlB95QnHt9Qwe0htiIo-kdP",
        currentProgress: 4,
        totalProgress: 7,
        isCurrentUser: true,
      },
      {
        rank: 5,
        name: "Emma R.",
        avatar:
          "https://lh3.googleusercontent.com/aida-public/AB6AXuBilDLodeFOHJFvd6Eygls9NxC8gOF52ry-tuLd3vI5scVTYtxXItRaDNJd05ZyJlBMlPCzjTOObCHATRyZEhYko0GwvOgtZbYqHxTitqhDMSTRlpoFQ4ht_o2ylb1LKkFTGsgQoMcNE-YHk3iTq3T1n2cGe1wMSiBMLU2mvHZNpMlA0m2Y1XTOMxMwzVXJqoRfYR9o1lvJ9HUswXCNnMkTl0pD_L0Hkfr8nOmWL_B10NtAlocyd9WTg3ocLO6N83dsE1wOh7Jzgamw",
        currentProgress: 3,
        totalProgress: 7,
      },
    ],
    participantAvatars: [
      "https://lh3.googleusercontent.com/aida-public/AB6AXuBilDLodeFOHJFvd6Eygls9NxC8gOF52ry-tuLd3vI5scVTYtxXItRaDNJd05ZyJlBMlPCzjTOObCHATRyZEhYko0GwvOgtZbYqHxTitqhDMSTRlpoFQ4ht_o2ylb1LKkFTGsgQoMcNE-YHk3iTq3T1n2cGe1wMSiBMLU2mvHZNpMlA0m2Y1XTOMxMwzVXJqoRfYR9o1lvJ9HUswXCNnMkTl0pD_L0Hkfr8nOmWL_B10NtAlocyd9WTg3ocLO6N83dsE1wOh7Jzgamw",
      "https://lh3.googleusercontent.com/aida-public/AB6AXuBbHsu0Bd6Qje8FCZRSZoHRBtbY2QtGQG8sbmCs4UufQ3G9nBhFNex7UpyWWbxs-l1oAMSXh3_t_fZelwv4kgfo0wiZwGuKlj2rYpdSOvzlJvf6Mkz1Kb_7gKSHxl_466OsdqBF-79X9lXbQMtz505-WifwxmtmxPgt47SE9XVNFugHzFHfwMzdBWhkrcjAuAhyWd21yNa7euBxAseojC_q9wtVWSDS4BZptCi7HLgU_ek8wdmEfvE9nqFB3OyQUyMq3OHok3NvDLPw",
      "https://lh3.googleusercontent.com/aida-public/AB6AXuCqY1PnQ14AV25TbTNJaFTtjcBiRhhcI_x2tuJn7Qbvz14OYqjk5jQ9vCAtLj2bGlvqSXGWF5Jfe0xAaDN_0qcVlsVLIqDeuusJX3Nx_K0Jn_6S_uUMEFualWqSRurBna1DJzxyEWbGw_d6YQg2JRwQ5icseXN63BJARrDAMsqGmrNMD2n5UOcCBmRNdNd31rBmu1a74P1mYXEJJ2FBh_7KHtGen7PoFdYgn_O8uw_KzRZbYA5megtO7F-h019fFfF-bEantqeVK6E9",
    ],
    userStats: {
      userAvatar:
        "https://lh3.googleusercontent.com/aida-public/AB6AXuDymDmkA1gaSpxj7gJ5FCEFLlp31mNaOxXVvNNe_4O9Qs_10j6Ji35xsOKwLF3Pl5MDxl_d7L0A4m_AyhrEQmRhFP0trRPMa9mGV0tMVQcoPYLTdOXZ_huFHuNQ5hPMHCk4jBVzF6LCl0z-DKieVu_1KpZUS-pm9kOOmXWSwyvuq1OzzFwFQfIkTO5Ri4yUDsjoCSgh-SOQ5wDnIfW0yfoBqE6qDyfPHW4l5ruZLOAik1P98C4qwcpriSlB95QnHt9Qwe0htiIo-kdP",
      totalDistance: 32.5,
      distanceGoal: 50,
      averagePace: "6'10\"",
      longestRun: 8.2,
      activeDays: 4,
      totalActiveDays: 4,
      recentActivities: [
        { label: "Today", distance: 3.5, status: "completed" as const },
        { label: "Yesterday", distance: 4.2, status: "completed" as const },
      ],
    },
  },
};

export type ChallengeData = (typeof MOCK_CHALLENGES_DATA)["1"];
